import { InlineNotice } from "../../components/InlineNotice/InlineNotice";
import { Spinner } from "../../components/Spinner/Spinner";
import { Button } from "../../components/Button/Button";
import { IconStore } from "../../components/icons/icons";
import { formatEntryDateTime } from "../../lib/format";
import type { MemberAnalytics } from "../../types";
import styles from "./AnalyticsPanel.module.css";

export type AnalyticsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; analytics: MemberAnalytics };

interface AnalyticsPanelProps {
  tenantName: string;
  state: AnalyticsState;
  onRetry: () => void;
}

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function formatMonth(month: string): string {
  const parsed = new Date(`${month}-01T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return month;
  return MONTH_FORMATTER.format(parsed);
}


export function AnalyticsPanel({ tenantName, state, onRetry }: AnalyticsPanelProps) {
  if (state.status === "loading") {
    return (
      <div className={styles.centerState}>
        <Spinner label={`Loading ${tenantName}'s analytics`} />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className={styles.centerState}>
        <InlineNotice tone="error">{state.message}</InlineNotice>
        <Button type="button" variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  const { analytics } = state;

  if (analytics.totalMembers === 0) {
    return (
      <div className={styles.empty}>
        <IconStore className={styles.emptyIcon} />
        <p>No one has joined {tenantName} yet. Analytics will populate here as shoppers sign up.</p>
      </div>
    );
  }

  const maxMonthly = Math.max(1, ...analytics.monthlySignups.map((entry) => entry.count));

  return (
    <div className={styles.wrap}>
      <div>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionHeading}>Membership summary</h3>
          <span className={styles.generated}>Updated {formatEntryDateTime(analytics.generatedAt)}</span>
        </div>
        <dl className={styles.stats}>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Total members</dt>
            <dd className={`${styles.statValue} tabular-nums`}>{analytics.totalMembers.toLocaleString()}</dd>
          </div>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Joined in last 30 days</dt>
            <dd className={`${styles.statValue} tabular-nums`}>{analytics.recentMembers.toLocaleString()}</dd>
          </div>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Opted into network offers</dt>
            <dd className={`${styles.statValue} tabular-nums`}>{analytics.networkOfferMembers.toLocaleString()}</dd>
          </div>
          <div className={styles.stat}>
            <dt className={styles.statLabel}>Network offer rate</dt>
            <dd className={`${styles.statValue} tabular-nums`}>
              {Number.isInteger(analytics.networkOfferRate)
                ? analytics.networkOfferRate
                : analytics.networkOfferRate.toFixed(1)}
              %
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionHeading}>Six-month signup trend</h3>
        </div>
        <table className={styles.trendTable}>
          <caption className="visually-hidden">Monthly signups for {tenantName}, oldest to newest</caption>
          <tbody>
            {analytics.monthlySignups.map((entry) => (
              <tr key={entry.month}>
                <th scope="row" className={styles.trendMonth}>
                  {formatMonth(entry.month)}
                </th>
                <td className={styles.trendBarCell}>
                  <span
                    className={styles.trendBar}
                    style={{
                      width:
                        entry.count === 0
                          ? 0
                          : `${Math.max(4, (entry.count / maxMonthly) * 100)}%`,
                    }}
                  />
                </td>
                <td className={`${styles.trendCount} tabular-nums`}>{entry.count.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
